<script setup lang="ts">
import { ChevronDown, Search } from 'lucide-vue-next';
import { cn } from '#shared/utils';
import {
	DEFAULT_NUTRITION_CHART_PARAM,
	getParamLabel,
	NUTRITION_CHART_PARAMS,
	type NutritionChartParamKey,
	type NutritionChartParamOption,
} from './nutrition-chart-params';

const model = defineModel<NutritionChartParamKey>({ default: DEFAULT_NUTRITION_CHART_PARAM });

const open = ref(false);
const search = ref('');

const filteredOptions = computed(() => {
	const q = search.value.trim().toLowerCase();
	if (!q) return NUTRITION_CHART_PARAMS;
	return NUTRITION_CHART_PARAMS.filter((opt) =>
		opt.label.toLowerCase().includes(q)
	);
});

const selectedOption = computed((): NutritionChartParamOption | undefined => {
	return NUTRITION_CHART_PARAMS.find((p) => p.key === model.value);
});

function select(key: NutritionChartParamKey) {
	model.value = key;
	open.value = false;
	search.value = '';
}

function onOpenChange(isOpen: boolean) {
	open.value = isOpen;
	if (!isOpen) search.value = '';
}
</script>

<template>
	<Popover v-model:open="open" @update:open="onOpenChange">
		<PopoverTrigger as-child>
			<button
				type="button"
				class="inline-flex items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-w-[220px]"
				aria-haspopup="listbox"
				:aria-expanded="open"
			>
				<span class="truncate">{{ selectedOption?.label ?? getParamLabel(model) }}</span>
				<ChevronDown class="h-4 w-4 shrink-0 opacity-50" />
			</button>
		</PopoverTrigger>
		<PopoverContent class="w-[var(--radix-popover-trigger-width)] p-0" align="start">
			<Command
				:model-value="model"
				class="rounded-lg border-0 shadow-none"
				@update:model-value="select($event as NutritionChartParamKey)"
			>
				<div class="flex items-center border-b border-input px-3">
					<Search class="mr-2 h-4 w-4 shrink-0 opacity-50" />
					<input
						v-model="search"
						type="text"
						placeholder="Cerca parametro..."
						:class="cn(
							'flex h-9 w-full rounded-md bg-transparent py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-0'
						)"
					/>
				</div>
				<CommandList>
					<CommandEmpty>Nessun parametro trovato.</CommandEmpty>
					<CommandGroup>
						<CommandItem
							v-for="opt in filteredOptions"
							:key="opt.key"
							:value="opt.key"
							class="cursor-pointer"
						>
							{{ opt.label }}
							<span v-if="opt.unit" class="ml-1 text-muted-foreground">({{ opt.unit }})</span>
						</CommandItem>
					</CommandGroup>
				</CommandList>
			</Command>
		</PopoverContent>
	</Popover>
</template>
